// useProgressiveReveal — bounded initial render for large lists.
//
// The Exchange now fetches the entire board (1000+ priced selections at
// substrate scale). Mounting all of them at once would choke first paint, so
// we render a bounded slice and grow it as a sentinel scrolls into view
// (IntersectionObserver, no dependency). Works for any layout — the caller
// just slices its array to `visible` and drops `sentinelRef` after the list.
//
// This bounds the INITIAL render and loads progressively; it is not full
// windowing (revealed nodes stay mounted). That's the right trade-off here:
// zero new deps, layout-agnostic (row list AND responsive grid), no column
// math. Swap for a virtualizer later only if a fully-scrolled board lags.

import { useEffect, useRef, useState } from "react";

export interface ProgressiveReveal {
  /** Number of items to render right now (<= total). */
  visible: number;
  /** Attach to a node rendered AFTER the list; scrolling to it reveals more. */
  sentinelRef: React.RefObject<HTMLDivElement>;
  /** True while more items remain to reveal. */
  hasMore: boolean;
}

export function useProgressiveReveal(
  total: number,
  opts: { initial?: number; step?: number } = {},
): ProgressiveReveal {
  const initial = opts.initial ?? 150;
  const step = opts.step ?? 120;
  const [count, setCount] = useState(initial);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Reset to the top whenever the list size changes (e.g. filters applied).
  useEffect(() => {
    setCount(initial);
  }, [total, initial]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || count >= total) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setCount((c) => Math.min(total, c + step));
        }
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [count, total, step]);

  const visible = Math.min(count, total);
  return { visible, sentinelRef, hasMore: visible < total };
}
